import { Key, ReactNode, useEffect } from 'react'
import { useBrandVisualKits } from 'hooks/queries/workspaces/useBrandVisualKits'
import Styles from './BrandKitInlineSelect.module.scss'
import { Item } from '@react-stately/collections'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Plus } from 'blaze-ui/icons'
import { Menu } from 'components/Menu/Menu'
import { DirectionalChevron } from 'components/DirectionalChevron/DirectionalChevron'
import { useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { FormButton } from '../FormButton/FormButton'
import { Brand } from 'blaze-ui/icons'
import { BrandWizardIntegrationSelection } from 'components/BrandWizardIntegrationSelection/BrandWizardIntegrationSelection'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BrandVisualKit } from 'types'
import { Placement } from '@floating-ui/core'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface Props {
  onBrandKitChange: (brandKit?: BrandVisualKit) => void
  selectedBrandKitId?: number
  triggerComponent?: ReactNode
  popoverClassName?: string
  placement?: Placement
}

export const BrandKitInlineSelect = ({
  onBrandKitChange,
  selectedBrandKitId,
  triggerComponent,
  popoverClassName,
  placement = 'bottom-start',
}: Props) => {
  const brandVisualKitsQuery = useBrandVisualKits()
  const brandVisualKits = brandVisualKitsQuery.data?.brand_visual_kits
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer
  const { isMobile } = useResponsiveDesign()
  const [isOpen, setIsOpen] = useState(false)
  const { openModal, closeStack } = useModals()
  const selectedBrandKit = brandVisualKits?.find((bk) => bk.id === selectedBrandKitId)

  const openCreateModal = () => {
    openModal(
      BrandWizardIntegrationSelection,
      {
        onConfirm: (kit) => {
          onBrandKitChange(kit)
          closeStack()
        },
      },
      { newStack: true },
    )
  }

  useEffect(() => {
    const latestBrandKit = brandVisualKits && brandVisualKits[brandVisualKits.length - 1]

    if (brandVisualKitsQuery.isFetched && latestBrandKit && !selectedBrandKitId) {
      onBrandKitChange(latestBrandKit)
    }
  }, [brandVisualKits]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!brandVisualKits) return null

  return (
    <MenuTrigger onOpenChange={setIsOpen} popoverClassName={popoverClassName} placement={placement}>
      {!!triggerComponent ? (
        <>{triggerComponent}</>
      ) : (
        <FormButton
          endIcon={() => <DirectionalChevron direction={isOpen ? 'up' : 'down'} />}
          variant="secondary"
          aria-label="Select your brand style"
          size="lg"
          justifyContent="space-between"
          fullWidth={isMobile}
        >
          <span className={Styles.buttonText}>{selectedBrandKit?.name || 'Select a Brand Style'}</span>
        </FormButton>
      )}
      <Menu
        showCheckmark
        selectionMode="multiple"
        selectedKeys={[selectedBrandKitId?.toString() as Key]}
        onAction={(key) => {
          if (key === 'add_new') {
            return openCreateModal()
          }

          const newBrandKit = brandVisualKits?.find((bk) => bk.id === parseInt(key as string))
          onBrandKitChange(newBrandKit?.id !== selectedBrandKitId ? newBrandKit : undefined)
        }}
      >
        {brandVisualKits.map((brandKit) => {
          return (
            <Item key={brandKit.id} textValue={brandKit.name}>
              <span className={Styles.brandStyleLogo}>
                {brandKit.primary_logo?.media_file.source_url ? (
                  <img src={brandKit.primary_logo.media_file.source_url} alt="brand style logo" />
                ) : (
                  <Brand />
                )}
              </span>
              <span className={Styles.kitName}>{brandKit.name}</span>
            </Item>
          )
        })}
        {!isVirtualMarketer && (
          <Item key="add_new">
            <span className={Styles.createBrandStyle}>
              <Plus color="var(--selected-fill)" /> Create New Brand Style
            </span>
          </Item>
        )}
      </Menu>
    </MenuTrigger>
  )
}
